// src/screens/DebtLever.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Brief 46B Section 5 — Debt lever: first screen in Your Plan.
// Always visible. Zero-debt shows "complete" state. Has-debt shows timeline.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { ClientData } from '../types/ClientData';
import { formatCurrency } from '../utils/format';
import { useCommaInput } from '../utils/useCommaInput';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

const fmt = formatCurrency;

export default function DebtLever({ data, onUpdate }: Props) {
  const totalDebt = data.consumerDebtTotal || 0;
  const monthlyPayment = data.consumerDebtMonthlyPayment || 0;
  const highestAPR = data.consumerDebtHighestAPR || 0;

  const [extraMonthly, setExtraMonthly] = useState(data.planDebtExtraMonthly || 0);
  const extraInput = useCommaInput(extraMonthly, (v) => {
    setExtraMonthly(v);
    onUpdate({ planDebtExtraMonthly: v });
  });

  // ── Debt-free date calculation ──────────────────────────────────────────────
  const calcPayoffMonths = (balance: number, monthly: number, apr: number): number => {
    if (balance <= 0 || monthly <= 0) return 0;
    const monthlyRate = apr / 100 / 12;
    if (monthlyRate <= 0) return Math.ceil(balance / monthly);
    // If monthly payment doesn't cover interest, debt never pays off
    const interest = balance * monthlyRate;
    if (monthly <= interest) return 999;
    const months = -Math.log(1 - (balance * monthlyRate) / monthly) / Math.log(1 + monthlyRate);
    return Math.ceil(months);
  };

  const basePayoffMonths = calcPayoffMonths(totalDebt, monthlyPayment, highestAPR);
  const acceleratedPayoffMonths = calcPayoffMonths(totalDebt, monthlyPayment + extraMonthly, highestAPR);

  const basePayoffYears = Math.round(basePayoffMonths / 12 * 10) / 10;
  const acceleratedPayoffYears = Math.round(acceleratedPayoffMonths / 12 * 10) / 10;
  const monthsSaved = basePayoffMonths - acceleratedPayoffMonths;

  // ── Interest calculation ────────────────────────────────────────────────────
  const calcTotalInterest = (balance: number, monthly: number, apr: number, months: number): number => {
    if (balance <= 0 || monthly <= 0 || months <= 0) return 0;
    const monthlyRate = apr / 100 / 12;
    let remaining = balance;
    let totalInterest = 0;
    for (let m = 0; m < Math.min(months, 600); m++) {
      const interest = remaining * monthlyRate;
      totalInterest += interest;
      remaining = Math.max(0, remaining + interest - monthly);
      if (remaining <= 0) break;
    }
    return totalInterest;
  };

  const baseInterest = calcTotalInterest(totalDebt, monthlyPayment, highestAPR, basePayoffMonths);
  const acceleratedInterest = calcTotalInterest(totalDebt, monthlyPayment + extraMonthly, highestAPR, acceleratedPayoffMonths);
  const interestSaved = Math.max(0, baseInterest - acceleratedInterest);

  // ── Chart data: debt balance over time ──────────────────────────────────────
  const chartData = useMemo(() => {
    if (totalDebt <= 0 || monthlyPayment <= 0) return [];
    const monthlyRate = highestAPR / 100 / 12;
    const maxMonths = Math.min(basePayoffMonths + 12, 600);
    const points: Array<{ month: number; base: number; accelerated: number }> = [];

    let baseBalance = totalDebt;
    let accelBalance = totalDebt;
    const totalMonthly = monthlyPayment + extraMonthly;

    for (let m = 0; m <= maxMonths; m++) {
      points.push({
        month: m,
        base: Math.round(Math.max(0, baseBalance)),
        accelerated: Math.round(Math.max(0, accelBalance)),
      });
      if (baseBalance <= 0 && accelBalance <= 0) break;
      baseBalance = Math.max(0, baseBalance * (1 + monthlyRate) - monthlyPayment);
      accelBalance = Math.max(0, accelBalance * (1 + monthlyRate) - totalMonthly);
    }
    return points;
  }, [totalDebt, monthlyPayment, extraMonthly, highestAPR, basePayoffMonths]);

  // ── Zero-debt state ─────────────────────────────────────────────────────────
  if (totalDebt <= 0) {
    return (
      <div className="min-h-screen flex">
        {/* Left panel */}
        <div className="w-2/5 bg-ma-navy text-white p-10 flex flex-col justify-center">
          <div className="mb-8">
            <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Baby Steps — Step 2</div>
            <div className="text-4xl font-black text-green-400 mb-2">Complete</div>
            <p className="text-white/60 text-sm">No consumer debt identified.</p>
          </div>

          <div className="border-t border-white/20 my-6" />

          <div className="mb-6">
            <div className="text-xs uppercase tracking-widest text-white/50 mb-1">Consumer Debt</div>
            <div className="text-3xl font-bold" style={{ color: '#C9A84C' }}>£0</div>
          </div>

          <div className="mt-auto pt-8">
            <p className="text-xs text-white/30">
              Step 2 is cleared — you can redirect this money to building wealth.
            </p>
          </div>
        </div>

        {/* Right panel */}
        <div className="w-3/5 bg-white p-10 flex items-center justify-center">
          <div className="text-center max-w-sm">
            <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">✓</span>
            </div>
            <h2 className="text-xl font-bold text-ma-navy mb-3">No consumer debt</h2>
            <p className="text-sm text-gray-500">
              With Step 2 cleared, every pound of surplus income can go straight
              to building your savings, pension and investments.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Has-debt state ──────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen flex">
      {/* Left panel */}
      <div className="w-2/5 bg-ma-navy text-white p-10 flex flex-col justify-center">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Baby Steps — Step 2</div>
          <div className="text-4xl font-black text-red-400 mb-2">Pay Off Debt</div>
        </div>

        <div className="border-t border-white/20 my-6" />

        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest text-white/50 mb-1">Total Consumer Debt</div>
          <div className="text-3xl font-bold text-red-400">{fmt(totalDebt)}</div>
        </div>

        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest text-white/50 mb-1">Current Monthly Payment</div>
          <div className="text-2xl font-bold" style={{ color: '#C9A84C' }}>{fmt(monthlyPayment)}/mo</div>
        </div>

        {highestAPR > 0 && (
          <div className="mb-6">
            <div className="text-xs uppercase tracking-widest text-white/50 mb-1">Highest APR</div>
            <div className="text-2xl font-bold text-white">{highestAPR.toFixed(1)}%</div>
          </div>
        )}

        <div className="border-t border-white/20 my-6" />

        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest text-white/50 mb-1">Debt-free in</div>
          <div className="text-3xl font-bold" style={{ color: '#64DAD2' }}>
            {extraMonthly > 0 ? `${acceleratedPayoffYears} years` : `${basePayoffYears} years`}
          </div>
          {extraMonthly > 0 && monthsSaved > 0 && (
            <p className="text-sm text-green-400 mt-1">{monthsSaved} months sooner than baseline</p>
          )}
        </div>

        {extraMonthly > 0 && interestSaved > 0 && (
          <div className="mb-6">
            <div className="text-xs uppercase tracking-widest text-white/50 mb-1">Interest Saved</div>
            <div className="text-2xl font-bold text-green-400">{fmt(Math.round(interestSaved))}</div>
          </div>
        )}

        <div className="mt-auto pt-8">
          <p className="text-xs text-white/30">
            Paying off debt first frees cash flow for savings and investments.
          </p>
        </div>
      </div>

      {/* Right panel */}
      <div className="w-3/5 bg-white p-10 flex flex-col justify-center">
        <h3 className="text-xl font-bold text-ma-navy mb-6">Debt Payoff Timeline</h3>

        {/* Extra payment input */}
        <div className="mb-6">
          <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">
            Additional monthly payment towards debt
          </label>
          <div className="flex items-center gap-2">
            <span className="text-gray-400">£</span>
            <input
              {...extraInput}
              className="w-40 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent bg-white"
              placeholder="0"
            />
            <span className="text-sm text-gray-400">/month</span>
          </div>
        </div>

        {/* Chart */}
        {chartData.length > 0 && (
          <div style={{ width: '100%', height: 320 }}>
            <ResponsiveContainer>
              <LineChart data={chartData} margin={{ top: 20, right: 30, bottom: 20, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="month"
                  tick={{ fill: '#6b7280', fontSize: 11 }}
                  label={{ value: 'Months', position: 'insideBottom', offset: -10, fill: '#9ca3af', fontSize: 11 }}
                />
                <YAxis
                  tickFormatter={(v: number) => `£${Math.round(v / 1000)}k`}
                  tick={{ fill: '#6b7280', fontSize: 11 }}
                />
                <Tooltip formatter={(v: any) => fmt(Number(v))} />
                <Line
                  type="monotone"
                  dataKey="base"
                  stroke="#1E3A5F"
                  strokeWidth={2}
                  dot={false}
                  name="Current payments"
                />
                {extraMonthly > 0 && (
                  <Line
                    type="monotone"
                    dataKey="accelerated"
                    stroke="#14B8A6"
                    strokeWidth={3}
                    dot={false}
                    name="With extra payments"
                  />
                )}
                <Legend verticalAlign="top" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Debt details table */}
        {data.consumerDebts.filter(d => d.balance > 0).length > 0 && (
          <div className="mt-6">
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Debt Details</h4>
            <div className="space-y-2">
              {data.consumerDebts.filter(d => d.balance > 0).map((debt, i) => (
                <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                  <div>
                    <div className="text-sm font-medium text-gray-700">{debt.name || `Debt ${i + 1}`}</div>
                    <div className="text-xs text-gray-400">{debt.apr}% APR | {fmt(debt.monthlyMinimum)}/mo minimum</div>
                  </div>
                  <div className="text-sm font-bold text-red-600">{fmt(debt.balance)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
